import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { applyNavigatedMessage, diffUrlToCommand } from './bridge-sync.logic';
import { hostBridgeFactorySingleton } from './host-bridge.singleton';
import { HostUrlCodec } from './host-url-codec';
import { getExpectedOrigin, IframeEmbedParams, IframeUrlState, urlStateToEmbedParams } from './iframe-url-state';
import { WarehousePresentation } from './host-bridge.messages';
import { QuickbaseHostBridge } from './quickbase-host-bridge';

export interface UseQuickbaseHostBridgeSyncOptions {
	embedUrl: string | null;
	codec: HostUrlCodec;
	reloadEmbedUrl(params: IframeEmbedParams): void;
	presentation?: WarehousePresentation;
}

export interface UseQuickbaseHostBridgeSyncResult {
	bridge: QuickbaseHostBridge | null;
	initialEmbedParams: IframeEmbedParams;
	reloadToken: number;
}

export function useQuickbaseHostBridgeSync({
	embedUrl,
	codec,
	reloadEmbedUrl,
	presentation = 'page',
}: UseQuickbaseHostBridgeSyncOptions): UseQuickbaseHostBridgeSyncResult {
	const location = useLocation();
	const navigate = useNavigate();

	const locationRef = useRef(location);
	locationRef.current = location;
	const navigateRef = useRef(navigate);
	navigateRef.current = navigate;
	const codecRef = useRef(codec);
	codecRef.current = codec;
	const reloadEmbedUrlRef = useRef(reloadEmbedUrl);
	reloadEmbedUrlRef.current = reloadEmbedUrl;

	const presentationRef = useRef(presentation);
	presentationRef.current = presentation;

	const [initialEmbedParams] = useState(() => urlStateToEmbedParams(codec.decode(location), presentation));

	const iframeStateRef = useRef<IframeUrlState | null>(null);
	const pendingUrlRef = useRef<string | null>(null);
	const [ready, setReady] = useState(false);
	const [reloadToken, setReloadToken] = useState(0);

	const bridge = useMemo(() => {
		if (embedUrl === null) return null;
		return hostBridgeFactorySingleton.get()({
			expectedOrigin: getExpectedOrigin(embedUrl),
			handlers: {
				onReady() {
					setReady(true);
				},
				onNavigated(message) {
					const isFirstNavigated = iframeStateRef.current === null;
					const { current } = locationRef;
					const urlRid = codecRef.current.decode(current).chain[0]?.rid ?? null;
					const next = applyNavigatedMessage(message, iframeStateRef.current, urlRid, presentationRef.current);
					iframeStateRef.current = next;

					const target = codecRef.current.encode(next, current);
					const targetHref = `${target.pathname}${target.search}`;
					const currentHref = `${current.pathname}${current.search}`;
					const converging = pendingUrlRef.current !== null;
					if (converging && (pendingUrlRef.current === targetHref || targetHref === currentHref)) {
						pendingUrlRef.current = null;
					}
					if (targetHref !== currentHref) {
						navigateRef.current(
							{ pathname: target.pathname, search: target.search, hash: current.hash },
							{ replace: isFirstNavigated || converging },
						);
					}
				},
			},
		});
	}, [embedUrl]);

	useEffect(() => {
		setReady(false);
		iframeStateRef.current = null;
		const { pathname, search } = locationRef.current;
		pendingUrlRef.current = `${pathname}${search}`;
	}, [bridge]);

	useEffect(() => {
		if (bridge === null || !ready || iframeStateRef.current === null) return;

		const urlState = codecRef.current.decode({ pathname: location.pathname, search: location.search });
		const command = diffUrlToCommand(urlState, iframeStateRef.current, presentationRef.current);
		if (command.kind !== 'none') {
			pendingUrlRef.current = `${location.pathname}${location.search}`;
		}

		if (command.kind === 'set-mode') {
			bridge.setMode(command.mode);
			iframeStateRef.current = { ...iframeStateRef.current, mode: command.mode };
		} else if (command.kind === 'open-record') {
			if (command.tab !== null) bridge.setTab(command.tab);
			bridge.openRecord({ chain: command.chain, mode: command.mode });
			iframeStateRef.current = {
				...iframeStateRef.current,
				chain: command.chain,
				...(command.mode !== null ? { mode: command.mode } : {}),
				...(command.tab !== null ? { tab: command.tab } : {}),
			};
		} else if (command.kind === 'set-tab') {
			bridge.setTab(command.tab);
			iframeStateRef.current = {
				...iframeStateRef.current,
				tab: command.tab,
				...(command.mode !== null ? { mode: command.mode } : {}),
			};
			if (command.mode !== null) bridge.setMode(command.mode);
		} else if (command.kind === 'reload') {
			iframeStateRef.current = null;
			setReloadToken((token) => token + 1);
			reloadEmbedUrlRef.current(command.params);
		}
	}, [location, bridge, ready]);

	return { bridge, initialEmbedParams, reloadToken };
}
